import { createFileRoute, Link } from "@tanstack/react-router";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@patient-watch/ui";
import landingQrCode from "../assets/landing_qr_code.svg";

export const Route = createFileRoute("/recruitment")({
    component: RecruitmentPage,
});

function RecruitmentPage() {
    return (
        <div className="p-4">
            <div className="space-y-8 mx-auto max-w-4xl">
                {/* Header */}
                <div className="space-y-4 text-center">
                    <h1 className="font-bold text-slate-800 text-4xl">
                        Seafarers' Continuous Glucose Monitoring Study
                    </h1>
                    <p className="text-slate-600 text-xl">Research Project ID: FHMS 23-24 062 | EGA/ERM ID 2469</p>
                    <p className="text-slate-700 text-lg">
                        Coordinated by The University of Surrey, supported by the MCA
                    </p>
                </div>

                {/* Study Overview */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-indigo-600 text-2xl">Study Overview</CardTitle>
                        <CardDescription className="text-lg">
                            The SeaGM Study - First step towards finding safe ways for seafarers with diabetes to work
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 text-md text-slate-700">
                        <div className="bg-green-50 p-4 border border-green-200 rounded-lg">
                            <h3 className="mb-2 font-semibold text-green-800">🎯 Study Purpose</h3>
                            <p className="text-green-700">
                                <strong>
                                    This study is the first step towards finding safe ways for seafarers with diabetes
                                    to work.
                                </strong>
                            </p>
                        </div>
                        <p>
                            This study aims to explore the use of continuous glucose monitoring (CGM) in seafarers and
                            fishers with insulin-treated diabetes who hold a valid ENG1 or ML5 certificate, or who are
                            working on crafts less than ten metres.
                        </p>
                        <p>
                            <strong>Our goal:</strong> To gather evidence that will help determine whether current
                            restrictions for seafarers with diabetes are appropriate or whether changes to medical
                            standards are indicated, potentially allowing more flexible and safe working arrangements at
                            sea/harbour.
                        </p>
                    </CardContent>
                </Card>

                {/* Recruitment Instructions */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-indigo-600 text-2xl">Recruitment Instructions for Doctors</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-slate-700">
                        <div className="bg-blue-50 p-4 border border-blue-200 rounded-lg">
                            <h3 className="mb-2 font-semibold text-blue-800 text-lg">
                                What to do when examining a seafarer:
                            </h3>
                            <ol className="space-y-2 text-blue-700 text-md list-decimal list-inside">
                                <li>
                                    If you examine a seafarer or fisher with insulin-treated diabetes, discuss this
                                    study with them
                                </li>
                                <li>Provide them with the QR code below to access study information</li>
                                <li>
                                    Explain that participation is entirely their choice and will not affect their
                                    fitness to work at sea/harbour
                                </li>
                                <li>Encourage them to contact the study coordinators if interested</li>
                            </ol>
                        </div>

                        <div className="bg-amber-50 p-4 border border-amber-200 rounded-lg">
                            <h3 className="mb-2 font-semibold text-amber-800 text-lg">Important Note:</h3>
                            <p className="text-amber-700 text-md">
                                Participation in this study will in no way affect the decision regarding their fitness
                                to work at sea/harbour. This is purely for research purposes.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* QR Code Section */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-indigo-600 text-2xl">
                            📱 Patient Recruitment QR Code
                        </CardTitle>
                        <CardDescription>
                            Show this QR code to patients for easy access to study information
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center space-y-4">
                        <div className="bg-white p-1 border-2 border-slate-300 border-dashed rounded-[1rem]">
                            <div className="flex justify-center items-center">
                                <img
                                    src={landingQrCode}
                                    alt="SeaGM Study QR Code"
                                    className="w-full max-w-[min(80vw,250px)]"
                                />
                            </div>
                        </div>
                        <p className="max-w-md text-slate-600 text-sm text-center">
                            Patients can scan this QR code to access detailed study information, participant information
                            sheet, and contact details.
                        </p>
                    </CardContent>
                </Card>

                {/* Study Details */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-indigo-600 text-2xl">Study Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6 text-slate-700">
                        <div>
                            <h3 className="mb-2 font-semibold text-lg">Study Duration:</h3>
                            <p className="text-md">1 year with 4 virtual meetings with study organisers</p>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-lg">What participants will do:</h3>
                            <ul className="space-y-1 ml-4 text-md list-disc list-inside">
                                <li>Wear a Dexcom G7® CGM device throughout the study</li>
                                <li>Complete an initial questionnaire about current glucose monitoring</li>
                                <li>Use a mobile app to track time at sea/harbour and glucose events</li>
                                <li>Text daily at 5pm with study information</li>
                                <li>Report any glucose issues (high/low/none) and other issues</li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="mb-2 font-semibold text-lg">Daily Reporting (5pm text):</h3>
                            <div className="bg-slate-50 p-4 rounded-lg">
                                <ul className="space-y-2 text-md">
                                    <li>• Were you at sea/harbour today: Yes/No</li>
                                    <li>• If yes: All day: Yes/No</li>
                                    <li>• If no: Start Time, End Time</li>
                                    <li>• Any glucose issues: High/Low/None</li>
                                    <li>• Any other issues: Free Text (100 characters)</li>
                                </ul>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Current Restrictions */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-indigo-600 text-2xl">
                            Current Restrictions for Seafarers with Diabetes
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-md text-slate-700">
                        <p>Currently, seafarers with insulin-treated diabetes face these restrictions:</p>
                        <ul className="space-y-1 ml-4 list-disc list-inside">
                            <li>No lone working</li>
                            <li>No solo watchkeeping</li>
                            <li>No working at heights</li>
                            <li>Geographical restriction on ENG1 unless excellent control demonstrated</li>
                            <li>May serve worldwide only on vessels with a ship's doctor (MSN 1886 3.4)</li>
                        </ul>
                        <div className="bg-blue-50 mt-4 p-4 border border-blue-200 rounded-lg">
                            <h3 className="mb-2 font-semibold text-blue-800">💡 Study Impact</h3>
                            <p className="text-blue-700 text-sm">
                                This study aims to provide evidence on whether these restrictions are appropriate given
                                modern CGM technology capabilities.{" "}
                                <strong>
                                    Our research could lead to updated medical standards that allow seafarers with
                                    diabetes to work more safely and effectively at sea/harbour.
                                </strong>
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Contact Information */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-indigo-600 text-2xl">Contact Information</CardTitle>
                        <CardDescription>For questions about the study or recruitment</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* University of Surrey Contacts */}
                        <div>
                            <h3 className="mb-3 font-semibold text-slate-800 text-lg">University of Surrey</h3>
                            <div className="space-y-3">
                                <div className="flex items-start gap-3">
                                    <span className="text-indigo-600">📧</span>
                                    <div className="text-slate-700 text-sm">
                                        <p className="font-medium">Prof David Russell-Jones</p>
                                        <p className="text-slate-600 text-sm">
                                            Professor in Diabetes and Endocrinology
                                        </p>
                                        <a
                                            href="mailto:d.russell-jones@surrey.ac.uk"
                                            className="text-indigo-600 hover:text-indigo-800 underline"
                                        >
                                            d.russell-jones@surrey.ac.uk
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <span className="text-indigo-600">📞</span>
                                    <div className="text-slate-700 text-sm">
                                        <p className="font-medium">Dr Fariba Shojaee-Moradie</p>
                                        <p className="text-slate-600 text-sm">Direct line: 01483 688644</p>
                                        <p className="text-slate-600 text-sm">Mobile: 07962215473</p>
                                        <a
                                            href="mailto:f.shojaee-moradie@surrey.ac.uk"
                                            className="text-indigo-600 hover:text-indigo-800 underline"
                                        >
                                            f.shojaee-moradie@surrey.ac.uk
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* MCA Contact */}
                        <div>
                            <h3 className="mb-3 font-semibold text-slate-800 text-lg">
                                Maritime and Coastguard Agency (MCA)
                            </h3>
                            <div className="flex items-start gap-3">
                                <span className="text-indigo-600">📧</span>
                                <div className="text-slate-700 text-sm">
                                    <p className="font-medium">Dr Sue Stannard</p>
                                    <a
                                        href="mailto:sue.stannard@mcga.gov.uk"
                                        className="text-indigo-600 hover:text-indigo-800 underline"
                                    >
                                        sue.stannard@mcga.gov.uk
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* University Address */}
                        <div>
                            <h3 className="mb-3 font-semibold text-slate-800 text-lg">University Address</h3>
                            <div className="flex items-start gap-3">
                                <span className="text-indigo-600">📍</span>
                                <div className="text-slate-700 text-sm">
                                    <p>School of Biosciences</p>
                                    <p>Faculty of Health and Medical Sciences</p>
                                    <p>University of Surrey</p>
                                    <p>Leggett Building, Daphne Jackson Rd</p>
                                    <p>Manor Park, Guildford GU2 7WG</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Funding Information */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-indigo-600 text-2xl">Study Funding</CardTitle>
                    </CardHeader>
                    <CardContent className="text-slate-700">
                        <p className="text-md">
                            This study is funded by The Seafarers' Charity and Fishmongers Company, with support from
                            the Maritime and Coastguard Agency (MCA).
                        </p>
                        <p className="mt-2 text-md">
                            The study has been approved by the Ethics Committee at the University of Surrey.
                        </p>
                    </CardContent>
                </Card>

                <Button asChild variant="default">
                    <Link to="/">Back to Home</Link>
                </Button>

                {/* Footer */}
                <div className="py-8 border-slate-200 border-t text-center">
                    <p className="text-slate-600 text-lg">
                        Thank you for your assistance in recruiting participants for this important study.
                    </p>
                    <p className="mt-2 text-md text-slate-500">
                        Your support helps advance understanding of diabetes management at sea/harbour.
                    </p>
                </div>
            </div>
        </div>
    );
}
